import React, {
  useState,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import DataTable from './ChatDataTable.js';
import { getCookie, setCookie } from '../cookieUtils.js';
import './Dashboard.css';
import AdvancedControls from '../components/AdvancedControls.jsx';
import ChatMessage from './components/ChatMessage.js';
import ModelSelector from './components/ModelSelector.js';
import DataVisualization from './components/DataVisualization.js';
import { useChatHandlers } from './hooks/useChatHandlers.js';
import { useModelManagement } from './hooks/useModelManagement.js';

export function ChatContainer(
  {
    isDarkMode,
    isMobile,
    windowWidth,
    Ampersandposium,
    display,
    reloadData,
    setReloadData,
    showDelete,
    userEmail,
    wait,
    styles,
    setNotificationMessage,
  },
  ref
) {
  // =======================
  // State variables
  // =======================
  const [isCopied, setIsCopied] = useState(false);
  const initialGetContextCookie = getCookie('getContext');
  const [getContext, setGetContext] = useState(
    initialGetContextCookie === undefined ? true : initialGetContextCookie === 'true'
  );
  const [nContext, setNContext] = useState(4);
  const [historySearch, setHistorySearch] = useState(false);
  const [embeddings, setEmbeddings] = useState('text-embedding-3-small');
  const [scrolls, setScrolls] = useState(0);

  // =======================
  // Flow detection
  // =======================
  const isFlowState = useMemo(() => {
    return Ampersandposium?.startsWith('Flow: ') || false;
  }, [Ampersandposium]);

  const vaultName = useMemo(() => {
    if (isFlowState) {
      return Ampersandposium.replace('Flow: ', '');
    }
    return Ampersandposium;
  }, [Ampersandposium, isFlowState]);

  // =======================
  // Custom hooks
  // =======================
  const modelManagement = useModelManagement(userEmail);
  const {
    chatMessage,
    setChatMessage,
    chatHistory,
    setChatHistory,
    isChatLoading,
    tempMessage,
    streamingCodeBlocks,
    messageEndRef,
    sendChatMessage,
    clearChat,
    handleTextareaKeydown,
  } = useChatHandlers(
    getContext,
    nContext,
    historySearch,
    embeddings,
    modelManagement.getSelectedModel,
    modelManagement.getSelectedThinkingLevel,
    isFlowState,
    vaultName
  );

  // =======================
  // Scroll to bottom logic
  // =======================
  const scrollToBottom = useCallback(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (scrolls === 0) {
      scrollToBottom();
      setScrolls(1);
    }
  }, [chatHistory, scrollToBottom]);

  // Persist getContext preference to cookie whenever it changes
  useEffect(() => {
    setCookie('getContext', getContext ? 'true' : 'false');
  }, [getContext]);

  // =======================
  // Chat history loading
  // =======================
  useEffect(() => {
    const loadChatHistory = async () => {
      const storedChatHistory = localStorage.getItem('chatHistory');
      if (storedChatHistory) {
        try {
          const parsedHistory = JSON.parse(storedChatHistory);
          const CHUNK_SIZE = 20;

          for (let i = 0; i < parsedHistory.length; i += CHUNK_SIZE) {
            const chunk = parsedHistory.slice(i, i + CHUNK_SIZE);
            setChatHistory((prev) => [...prev, ...chunk]);
            await new Promise((resolve) => setTimeout(resolve, 0));
          }
        } catch (error) {
          console.error('Failed to parse stored chat history:', error);
          setChatHistory([]);
        }
      }
    };
    loadChatHistory();
  }, []);

  // =======================
  // Chat history saving
  // =======================
  useEffect(() => {
    const saveTimeout = setTimeout(() => {
      if (chatHistory.length > 0) {
        localStorage.setItem('chatHistory', JSON.stringify(chatHistory));
      }
    }, 1000);

    return () => clearTimeout(saveTimeout);
  }, [chatHistory]);

  // =======================
  // Render
  // =======================
  return (
    <div
      className="response-container"
      id="chat-container"
      style={{
        ...styles.responseContainer,
        minHeight: '200px',
        display: 'flex',
        padding: isMobile ? '20px 6px' : '20px',
        height: 'auto',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Header with clear button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
          marginBottom: '20px',
        }}
      >
        <button
          id="clear-chat"
          className="link-button"
          onClick={clearChat}
          style={{ marginRight: isMobile ? '8px' : '2px' }}
        >
          Clear
        </button>
      </div>

      {/* Chat history */}
      <div style={{ width: '100%', marginBottom: '20px' }}>
        {chatHistory.map((chat, index) => (
          <div key={index} style={{ position: 'relative', marginBottom: '0.5rem' }}>
            <button
              onClick={() => {
                setChatHistory((prevHistory) => {
                  const newHistory = prevHistory.filter((_, i) => i !== index);
                  localStorage.setItem('chatHistory', JSON.stringify(newHistory));
                  return newHistory;
                });
              }}
              style={{
                position: 'absolute',
                right: '-5px',
                top: '0px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '1.2rem',
                opacity: 0.5,
                transition: 'opacity 0.2s',
                padding: '2px 6px',
                color: isDarkMode ? '#ff6b6b' : '#e74c3c',
                zIndex: 10
              }}
              onMouseEnter={(e) => (e.target.style.opacity = 1)}
              onMouseLeave={(e) => (e.target.style.opacity = 0.5)}
              title="Delete this message"
            >
              ×
            </button>
            <ChatMessage
              message={chat.message}
              isUserMessage={chat.isUserMessage}
              index={index}
              windowWidth={windowWidth}
              isDarkMode={isDarkMode}
              styles={styles}
              streamingCodeBlocks={streamingCodeBlocks}
            />
          </div>
        ))}
        {isChatLoading && tempMessage && (
          <ChatMessage
            message={tempMessage}
            isUserMessage={false}
            index={chatHistory.length}
            windowWidth={windowWidth}
            isDarkMode={isDarkMode}
            styles={styles}
            streamingCodeBlocks={streamingCodeBlocks}
          />
        )}
        <div ref={messageEndRef} />
      </div>

      {/* Loading spinner */}
      {isChatLoading && (
        <div className="loading-spinner" style={{ display: 'block' }}>
          <svg viewBox="0 0 50 50">
            <circle cx="25" cy="25" r="20" stroke="#00d67a" strokeWidth="5" fill="none" />
          </svg>
        </div>
      )}

      {/* Controls Section */}
      <div style={{ width: '100%' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: '10px',
            alignItems: isMobile ? 'stretch' : 'center',
            marginBottom: '10px',
          }}
        >
          <ModelSelector
            model={modelManagement.model}
            models={modelManagement.models}
            handleModelChange={modelManagement.handleModelChange}
            thinkingLevel={modelManagement.thinkingLevel}
            thinkingCapability={modelManagement.thinkingCapability}
            handleThinkingLevelChange={modelManagement.handleThinkingLevelChange}
            showCustomModelInput={modelManagement.showCustomModelInput}
            customModelName={modelManagement.customModelName}
            setCustomModelName={modelManagement.setCustomModelName}
            styles={styles}
            isFlow={isFlowState}
          />

          {!isFlowState && (
            <AdvancedControls
              isDarkMode={isDarkMode}
              getContext={getContext}
              setGetContext={setGetContext}
              nContext={nContext}
              setNContext={setNContext}
              historySearch={historySearch}
              setHistorySearch={setHistorySearch}
              embeddings={embeddings}
              setEmbeddings={setEmbeddings}
              styles={styles}
            />
          )}
        </div>
      </div>

      <textarea
        placeholder="Type your message here..."
        id="chat-message"
        rows={isMobile ? '8' : '5'}
        style={{ ...styles.textarea, paddingLeft: '8px' }}
        value={chatMessage}
        onChange={(e) => setChatMessage(e.target.value)}
        onKeyDown={handleTextareaKeydown}
      />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
        <button id="chat-submit" style={{ ...styles.button }} onClick={sendChatMessage}>
          Send
        </button>
      </div>

      <div style={{ marginTop: '100px' }}></div>

      {/* Optional DataTable if needed */}
      {display && !isFlowState && (
        <DataTable
          Ampersandposium={Ampersandposium}
          reloadData={reloadData}
          setReloadData={setReloadData}
          showDelete={showDelete}
          userEmail={userEmail}
          isDarkMode={isDarkMode}
          wait={wait}
          styles={styles}
        />
      )}

      {/* Data Visualization Component */}
      <DataVisualization
        Ampersandposium={Ampersandposium}
        isDarkMode={isDarkMode}
        styles={styles}
        display={display}
        isFlow={isFlowState}
      />
    </div>
  );
}

export default forwardRef(ChatContainer);
